import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as cornerstone from "@cornerstonejs/core";
import { ViewportType } from "@cornerstonejs/core/enums";
import toastContext from "../../../../context/toast/ToastContext";
import themeContext from "../../../../context/theme/ThemeContext";
import { initialize as initCornerstone, getAugToolGroup } from "../../../StudyViewer/helpers/CornerstoneService";
import hardcodedWebMetaDataProvider from "../../../StudyViewer/helpers/MetadataProvider";
import { TOAST_TYPE } from "../../../../constants/Constants";
import ModalMain from "../../../../components/Modal/ModalMain";
import QCModalHeader from "../../../../components/Modal/QCModalHeader";
import QCModalFooter from "../../../../components/Modal/QCModalFooter";

import Rectangle     from "../../../../components/SVGComponents/Tools/Rectangle";
import Ellipse       from "../../../../components/SVGComponents/Tools/Ellipse";
import Circle        from "../../../../components/SVGComponents/Tools/Circle";
import Bidirectional from "../../../../components/SVGComponents/Tools/Bidirectional";
import Annotation    from "../../../../components/SVGComponents/Tools/Annotation";
import Pan           from "../../../../components/SVGComponents/Tools/Pan";
import Zoom          from "../../../../components/SVGComponents/Zoom";
import RotateLeft    from "../../../../components/SVGComponents/Tools/RotateLeft";
import RotateRight   from "../../../../components/SVGComponents/Tools/RotateRight";

// Per-feature configuration. Both Fistulogram and Fibroid run the exact same
// annotation UI — only the endpoints, image, and a handful of IDs differ. Add
// new reference-image features by appending another config here and wiring it
// up at the callsite (see AlertSubmitSection.jsx).
export const FISTULOGRAM_CONFIG = {
  title:              "Fistulogram Image",
  referenceImagePath: "REFERENCE_IMAGE/FISTULOGRAM/download.jpg",
  loaderScheme:       "fisto",
  engineId:           "fistulogram-rendering-engine",
  viewportId:         "fistulogram-viewport",
  outputFileName:     "fistulogram.jpg",
  errorLabel:         "fistulogram",
  featureLabel:       "fistulogram",
  logTag:             "[Fisto]",
};

export const FIBROID_CONFIG = {
  title:              "Fibroid Image",
  referenceImagePath: "REFERENCE_IMAGE/FIBROID_MAPPING/fibroid_mapping.jpg",
  loaderScheme:       "fibroid",
  engineId:           "fibroid-rendering-engine",
  viewportId:         "fibroid-viewport",
  outputFileName:     "fibroid.jpg",
  errorLabel:         "fibroid",
  featureLabel:       "fibroid",
  logTag:             "[Fibroid]",
  // Fibroid is gynecological — saving for a male patient should prompt for confirmation.
  // The check is client-side: if patientSex === "M", a confirm dialog appears; missing /
  // unknown / "F" / "O" sex saves silently.
  femaleOnly: true,
};

// Inline polygon icon for the Flexpoly tool — a pentagon with vertex dots,
// distinct from Pencil's freehand stroke.
const PolygonIcon = ({ color }) => (
  <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon points="8,1.5 14,5.5 12,12 4,12 2,5.5" stroke={color} strokeWidth="1.2" fill="none" />
    <circle cx="8"  cy="1.5" r="1.2" fill={color} />
    <circle cx="14" cy="5.5" r="1.2" fill={color} />
    <circle cx="12" cy="12"  r="1.2" fill={color} />
    <circle cx="4"  cy="12"  r="1.2" fill={color} />
    <circle cx="2"  cy="5.5" r="1.2" fill={color} />
  </svg>
);

// Inline pencil icon — a diagonal pencil silhouette with a tip notch and an
// eraser-end notch. Used by the Freehand tool. The shared Pencil SVG component
// looks more like a paint-brush; this version visually reads as a pencil.
const PencilIconInline = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.5 1.5 L14.5 4.5 L5 14 L1.5 14.5 L2 11 Z"
      stroke={color}
      strokeWidth="1.2"
      strokeLinejoin="round"
      fill="none"
    />
    <line x1="3" y1="13" x2="4.5" y2="14.5" stroke={color} strokeWidth="1.2" />
    <line x1="10.5" y1="2.5" x2="13.5" y2="5.5" stroke={color} strokeWidth="1.2" />
  </svg>
);

// Inline line icon — a single diagonal stroke, Conveys "draw a line",
// not "measure a length".
const LineIconInline = ({ color }) => (
  <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="2.5" y1="11.5" x2="13.5" y2="2.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// Image-loader registration is keyed by scheme so each config registers its
// custom "{scheme}:" loader exactly once per app session. RGBA → 3-channel RGB
// conversion is required: Cornerstone's VTK pipeline expects 3 components and
// 4-channel input produces horizontal stripe artifacts.
const registeredLoaderSchemes = new Set();
function ensureLoaderRegistered(scheme) {
  if (registeredLoaderSchemes.has(scheme)) return;
  registeredLoaderSchemes.add(scheme);
  const prefixLen = scheme.length + 1;
  cornerstone.imageLoader.registerImageLoader(scheme, (imageId) => {
    const blobUrl = imageId.substring(prefixLen);
    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const rgbaData = ctx.getImageData(0, 0, w, h).data;
        const rgbData = new Uint8Array(w * h * 3);
        for (let i = 0, j = 0; i < rgbaData.length; i += 4, j += 3) {
          rgbData[j] = rgbaData[i]; rgbData[j+1] = rgbaData[i+1]; rgbData[j+2] = rgbaData[i+2];
        }
        resolve({ imageId, minPixelValue: 0, maxPixelValue: 255, slope: 1, intercept: 0,
          windowCenter: 128, windowWidth: 255, rows: h, columns: w, height: h, width: w,
          color: true, rgba: false, numberOfComponents: 3,
          getPixelData: () => rgbData, getCanvas: () => canvas, sizeInBytes: rgbData.byteLength });
      };
      img.onerror = () => reject(new Error(`${scheme}: loader failed`));
      img.src = blobUrl;
    });
    return { promise };
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Tool identifiers
// ─────────────────────────────────────────────────────────────────────────

const TOOL_RECT     = "Rectangle";
const TOOL_ELLIPSE  = "Ellipse";
const TOOL_CIRCLE   = "Circle";
const TOOL_LINE     = "Line";
const TOOL_BIDIR    = "Bidirectional";
const TOOL_ARROW    = "Arrow";
const TOOL_FREEHAND = "Freehand";
const TOOL_FLEXPOLY = "Flexpoly";       // click-to-place-vertex polygon
const TOOL_TEXT     = "Text";
const TOOL_ZOOM     = "Zoom";
const TOOL_PAN      = "Pan";

// Tools that produce a new shape on drag (mousedown → drag → mouseup commits).
// TOOL_FLEXPOLY is NOT here — it's click-to-place-vertex, handled in its own branch.
const DRAW_TOOLS = new Set([TOOL_RECT, TOOL_ELLIPSE, TOOL_CIRCLE, TOOL_LINE, TOOL_BIDIR, TOOL_ARROW, TOOL_FREEHAND, TOOL_TEXT]);

const TOOLBAR_GROUPS = [
  [
    { name: TOOL_ZOOM,     label: "Zoom"          },
    { name: TOOL_PAN,      label: "Pan"           },
  ],
  [
    { name: TOOL_ARROW,    label: "Annotation"    },
    { name: TOOL_FREEHAND, label: "Pencil"        },
    { name: TOOL_FLEXPOLY, label: "ROI"           },
    { name: TOOL_CIRCLE,   label: "Circle"        },
    { name: TOOL_LINE,     label: "Line"          },
    { name: TOOL_BIDIR,    label: "Bidirectional" },
    { name: TOOL_ELLIPSE,  label: "Ellipse"       },
    { name: TOOL_RECT,     label: "Rectangle"     },
  ],
];

const SHAPE_STROKE          = "red"; //#00ff00 (green)
const SHAPE_STROKE_SELECTED = "#00d4ff";
const SHAPE_STROKE_WIDTH    = 2;        // default; user can pick from STROKE_WIDTH_OPTIONS
const HIT_TOLERANCE_PX      = 6;
const FLEXPOLY_CLOSE_TOL    = 10;
// Discrete thickness presets exposed in the toolbar. The user picks one before drawing
// a shape; the chosen width is baked into the shape so changing the active thickness
// later does not retroactively modify shapes that have already been committed.
const STROKE_WIDTH_OPTIONS  = [1, 2, 4, 6];

// ─────────────────────────────────────────────────────────────────────────
// Geometry helpers (pure)
// ─────────────────────────────────────────────────────────────────────────

function distanceToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ddx = px - x1, ddy = py - y1;
    return Math.sqrt(ddx * ddx + ddy * ddy);
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = x1 + t * dx, projY = y1 + t * dy;
  const ddx = px - projX, ddy = py - projY;
  return Math.sqrt(ddx * ddx + ddy * ddy);
}

// eslint-disable-next-line no-unused-vars
function isPointInShape(shape, x, y) {
  const tol = HIT_TOLERANCE_PX;
  switch (shape.type) {
    case TOOL_RECT: {
      const xa = Math.min(shape.x1, shape.x2), xb = Math.max(shape.x1, shape.x2);
      const ya = Math.min(shape.y1, shape.y2), yb = Math.max(shape.y1, shape.y2);
      return x >= xa - tol && x <= xb + tol && y >= ya - tol && y <= yb + tol;
    }
    case TOOL_ELLIPSE: {
      const cx = (shape.x1 + shape.x2) / 2, cy = (shape.y1 + shape.y2) / 2;
      const rx = Math.abs(shape.x2 - shape.x1) / 2 + tol;
      const ry = Math.abs(shape.y2 - shape.y1) / 2 + tol;
      if (rx <= 0 || ry <= 0) return false;
      const ddx = (x - cx) / rx, ddy = (y - cy) / ry;
      return ddx * ddx + ddy * ddy <= 1;
    }
    case TOOL_CIRCLE: {
      const ddx = x - shape.cx, ddy = y - shape.cy;
      return Math.sqrt(ddx * ddx + ddy * ddy) <= shape.r + tol;
    }
    case TOOL_LINE:
    case TOOL_ARROW:
      return distanceToSegment(x, y, shape.x1, shape.y1, shape.x2, shape.y2) <= tol;
    case TOOL_BIDIR: {
      if (distanceToSegment(x, y, shape.x1, shape.y1, shape.x2, shape.y2) <= tol) return true;
      const lDx = shape.x2 - shape.x1, lDy = shape.y2 - shape.y1;
      const length = Math.sqrt(lDx * lDx + lDy * lDy);
      if (length === 0) return false;
      const midX = (shape.x1 + shape.x2) / 2;
      const midY = (shape.y1 + shape.y2) / 2;
      const perpX = -lDy / length, perpY = lDx / length;
      const shortHalf = length * 0.4;
      const sx1 = midX + perpX * shortHalf, sy1 = midY + perpY * shortHalf;
      const sx2 = midX - perpX * shortHalf, sy2 = midY - perpY * shortHalf;
      return distanceToSegment(x, y, sx1, sy1, sx2, sy2) <= tol;
    }
    case TOOL_FREEHAND:
      for (let i = 0; i < shape.points.length - 1; i++) {
        const [x1, y1] = shape.points[i];
        const [x2, y2] = shape.points[i + 1];
        if (distanceToSegment(x, y, x1, y1, x2, y2) <= tol) return true;
      }
      return false;
    case TOOL_FLEXPOLY: {
      const n = shape.points.length;
      for (let i = 0; i < n; i++) {
        const [x1, y1] = shape.points[i];
        const [x2, y2] = shape.points[(i + 1) % n];
        if (distanceToSegment(x, y, x1, y1, x2, y2) <= tol) return true;
      }
      return false;
    }
    case TOOL_TEXT: {
      const w = (shape.text || "").length * 7 + 4;
      return x >= shape.x - 2 && x <= shape.x + w && y >= shape.y - 14 && y <= shape.y + 4;
    }
    default:
      return false;
  }
}

function translateShape(shape, dx, dy) {
  switch (shape.type) {
    case TOOL_RECT:
    case TOOL_ELLIPSE:
    case TOOL_LINE:
    case TOOL_BIDIR:
    case TOOL_ARROW:
      return { ...shape, x1: shape.x1 + dx, y1: shape.y1 + dy, x2: shape.x2 + dx, y2: shape.y2 + dy };
    case TOOL_CIRCLE:
      return { ...shape, cx: shape.cx + dx, cy: shape.cy + dy };
    case TOOL_FREEHAND:
    case TOOL_FLEXPOLY:
      return { ...shape, points: shape.points.map(([x, y]) => [x + dx, y + dy]) };
    case TOOL_TEXT:
      return { ...shape, x: shape.x + dx, y: shape.y + dy };
    default:
      return shape;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────
//
// Props:
//   - studyParentId         id passed straight through to uploadFunc
//   - config                one of FISTULOGRAM_CONFIG / FIBROID_CONFIG 
//   - filePath              last-saved file path from context (null/"null" → use referenceImagePath)
//   - patientSex            DICOM patient sex code ("M" / "F" / "O" / null). Only consulted when
//                           config.femaleOnly is true (used to gate the Save with a confirm popup).
//   - onClose               called when the user closes / cancels the modal
//   - downloadImageAsBlob   (filePath) => Promise<Blob>  — feature-specific download
//   - uploadFunc            (studyParentId, file) => Promise — feature-specific upload
//   - deleteFunc            (studyParentId) => Promise — clears the saved image on the
//                           backend. Called when the user clicks Save with no annotations
//                           (typically after Reset), so the toolbar "edited" indicator clears.
const AnnotatedImageModal = ({
  studyParentId,
  config,
  filePath,
  patientSex,
  onClose,
  downloadImageAsBlob,
  uploadFunc,
  deleteFunc,
}) => {
  const { showToastv2 } = useContext(toastContext);
  const { getThemeColor, theme } = useContext(themeContext);
  const { t } = useTranslation();

  const { engineId, viewportId, loaderScheme, referenceImagePath, title, outputFileName, errorLabel, featureLabel, logTag, femaleOnly } = config;

  const viewportDivRef    = useRef(null);
  const blobUrlsRef       = useRef([]);
  const metaProviderRef   = useRef(null);
  const svgOverlayRef     = useRef(null);
  const currentImageIdRef = useRef(null);
  const navStartRef       = useRef(null);
  const activePointersRef = useRef(new Map());
  const pinchStartRef     = useRef(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [activeStrokeWidth, setActiveStrokeWidth] = useState(SHAPE_STROKE_WIDTH);
  const [strokeDropdownOpen, setStrokeDropdownOpen] = useState(false);
  const strokeDropdownRef = useRef(null);
  const [isLoading,  setIsLoading]  = useState(true);
  const [isSaving,   setIsSaving]   = useState(false);

  const [shapes,        setShapes]        = useState([]);
  const [drawing,       setDrawing]       = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [moveStart,     setMoveStart]     = useState(null);
  const [history,       setHistory]       = useState([{ shapes: [], imageId: null }]);
  const [historyIndex,  setHistoryIndex]  = useState(0);
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [previewPoint,   setPreviewPoint]   = useState(null);
  // Gender-conflict confirm popup (driven by config.femaleOnly + patientSex === "M").
  // Shown after the user clicks Save when the patient sex contradicts the feature's
  // intended population. Continue → proceeds to performSave(); Cancel → just hides
  // the popup and leaves the user in Edit mode with their annotations intact.
  const [showSexWarning, setShowSexWarning] = useState(false);
  // Confirmation popup for the "Clear" button.
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const commitToHistory = (nextShapes, nextImageId = currentImageIdRef.current) => {
    setShapes(nextShapes);
    setHistory((prev) => {
      const truncated = prev.slice(0, historyIndex + 1);
      truncated.push({ shapes: nextShapes, imageId: nextImageId });
      return truncated;
    });
    setHistoryIndex((i) => i + 1);
  };

  const restoreHistory = async (newIndex) => {
    const entry = history[newIndex];
    setHistoryIndex(newIndex);
    setShapes(entry.shapes);
    setSelectedIndex(null);
    setDrawing(null);
    setMoveStart(null);
    setPendingPolygon(null);
    setPreviewPoint(null);
    if (entry.imageId && entry.imageId !== currentImageIdRef.current) {
      try { await loadImage(entry.imageId); } catch (e) { console.error(`${logTag} restore image failed:`, e); }
    }
  };

  const handleUndo = () => {
    if (historyIndex <= 0) return;
    restoreHistory(historyIndex - 1);
  };

  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    restoreHistory(historyIndex + 1);
  };

  // Fetch the feature's image and return its "{scheme}:{blobUrl}" imageId.
  //   - default args            → last-saved image (uploaded if available, else reference)
  //   - { forceDefault: true }  → always the plain reference image (used by Reset)
  const fetchImageId = useCallback(async ({ forceDefault } = {}) => {
    const fp = forceDefault
      ? referenceImagePath
      : (filePath && filePath !== "null" ? filePath : referenceImagePath);
    // Skip the VIEW audit when this fetch is triggered by Reset (forceDefault=true) or
    // by the Reset → Save clear path. 
    const blob = await downloadImageAsBlob(fp, { skipAudit: !!forceDefault });
    if (!blob) throw new Error(`Failed to fetch ${errorLabel} image`);
    const blobUrl = URL.createObjectURL(blob);
    blobUrlsRef.current.push(blobUrl);
    return `${loaderScheme}:${blobUrl}`;
  }, [filePath, referenceImagePath, downloadImageAsBlob, errorLabel, loaderScheme]);

  const setupViewport = useCallback(async (imageId) => {
    if (!viewportDivRef.current) return;

    await initCornerstone(null);
    ensureLoaderRegistered(loaderScheme);

    const existingEngine = cornerstone.getRenderingEngine(engineId);
    if (existingEngine) existingEngine.destroy();
    const engine = new cornerstone.RenderingEngine(engineId);
    while (viewportDivRef.current.firstChild) viewportDivRef.current.removeChild(viewportDivRef.current.firstChild);

    engine.enableElement({ viewportId, type: ViewportType.STACK, element: viewportDivRef.current });

    const toolGroup = getAugToolGroup();
    if (toolGroup) toolGroup.addViewport(viewportId, engineId);

    // Generic metadata provider — handles any {scheme}:blob:... imageId.
    const schemePrefix = `${loaderScheme}:`;
    const metaProvider = (type, imgId) => {
      if (!imgId || typeof imgId !== "string" || !imgId.startsWith(schemePrefix)) return undefined;
      const webId = "web:" + imgId.substring(schemePrefix.length);
      return hardcodedWebMetaDataProvider(type, webId, [webId]);
    };
    cornerstone.metaData.addProvider(metaProvider, 10000);
    metaProviderRef.current = metaProvider;

    const viewport = engine.getViewport(viewportId);
    await viewport.setStack([imageId]);
    viewport.render();
    currentImageIdRef.current = imageId;
  }, [engineId, viewportId, loaderScheme]);

  // resetCamera=true → fit-to-viewport after the new image loads. Needed after Save
  // (the user's zoom-state would otherwise persist over the new full-image JPEG and
  // crop it to whatever the previous zoom showed) and after Reset / Reset→Save clear.
  // Undo / Redo leave the camera alone so restoring history feels continuous.
  const loadImage = async (imageId, { resetCamera = false } = {}) => {
    const engine = cornerstone.getRenderingEngine(engineId);
    const viewport = engine?.getViewport(viewportId);
    if (!viewport) return;
    await viewport.setStack([imageId]);
    if (resetCamera) viewport.resetCamera();
    viewport.render();
    currentImageIdRef.current = imageId;
  };

  useEffect(() => {
    let mounted = true;
    const startSetup = async () => {
      try {
        const imageId = await fetchImageId();
        if (!mounted) return;
        for (let i = 0; i < 100 && mounted; i++) {
          const el = viewportDivRef.current;
          if (el && el.offsetWidth > 0 && el.offsetHeight > 0) break;
          await new Promise((r) => setTimeout(r, 16));
        }
        if (!mounted) return;
        await setupViewport(imageId);
        if (mounted) {
          setHistory([{ shapes: [], imageId }]);
          setHistoryIndex(0);
          setIsLoading(false);
        }
      } catch (err) {
        console.error(`${logTag} mount error:`, err);
        showToastv2(TOAST_TYPE.ERROR, `Failed to load ${errorLabel} image`);
        if (mounted) setIsLoading(false);
      }
    };
    startSetup();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (metaProviderRef.current) {
        cornerstone.metaData.removeProvider(metaProviderRef.current);
        metaProviderRef.current = null;
      }
      const tg = getAugToolGroup();
      if (tg) { try { tg.removeViewports?.(engineId, viewportId); } catch (_) {} }
      const engine = cornerstone.getRenderingEngine(engineId);
      if (engine) { try { engine.destroy(); } catch (_) {} }
      blobUrlsRef.current.forEach((url) => { try { URL.revokeObjectURL(url); } catch (_) {} });
      blobUrlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => onClose();

  const handleToolSelect = (toolName) => {
    if (!isEditMode) return;
    setActiveTool(toolName);
    setSelectedIndex(null);
    setDrawing(null);
    setMoveStart(null);
    setPendingPolygon(null);
    setPreviewPoint(null);
    navStartRef.current = null;
  };

  const handleEdit = () => setIsEditMode(true);

  // ─────────────────────────────────────────────────────────────────────────
  // SVG annotation drawing + interaction
  // ─────────────────────────────────────────────────────────────────────────

  const svgPointFromEvent = (e) => {
    const rect = svgOverlayRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const createInitialShape = (tool, p) => {
    // strokeWidth is captured at draw-start time and baked into the shape, so changing
    // the active thickness later only affects subsequent shapes — not already-drawn ones.
    switch (tool) {
      case TOOL_RECT:
      case TOOL_ELLIPSE:
      case TOOL_LINE:
      case TOOL_BIDIR:
      case TOOL_ARROW:
        return { type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, strokeWidth: activeStrokeWidth };
      case TOOL_CIRCLE:
        return { type: tool, cx: p.x, cy: p.y, r: 0, strokeWidth: activeStrokeWidth };
      case TOOL_FREEHAND:
        return { type: tool, points: [[p.x, p.y]], strokeWidth: activeStrokeWidth };
      default:
        return null;
    }
  };

  const updateDrawingShape = (shape, p) => {
    switch (shape.type) {
      case TOOL_RECT:
      case TOOL_ELLIPSE:
      case TOOL_LINE:
      case TOOL_BIDIR:
      case TOOL_ARROW:
        return { ...shape, x2: p.x, y2: p.y };
      case TOOL_CIRCLE: {
        const dx = p.x - shape.cx, dy = p.y - shape.cy;
        return { ...shape, r: Math.sqrt(dx * dx + dy * dy) };
      }
      case TOOL_FREEHAND:
        return { ...shape, points: [...shape.points, [p.x, p.y]] };
      default:
        return shape;
    }
  };

  const isShapeMeaningful = (shape) => {
    if (!shape) return false;
    switch (shape.type) {
      case TOOL_RECT:
      case TOOL_ELLIPSE:
      case TOOL_LINE:
      case TOOL_BIDIR:
      case TOOL_ARROW:
        return Math.abs(shape.x2 - shape.x1) > 2 || Math.abs(shape.y2 - shape.y1) > 2;
      case TOOL_CIRCLE:
        return shape.r > 2;
      case TOOL_FREEHAND:
        return shape.points.length > 2;
      case TOOL_FLEXPOLY:
        return shape.points.length >= 3;
      case TOOL_TEXT:
        return !!(shape.text && shape.text.trim());
      default:
        return false;
    }
  };

  // ── Zoom / Pan ──
  const beginNavGesture = (e) => {
    const engine = cornerstone.getRenderingEngine(engineId);
    const viewport = engine?.getViewport(viewportId);
    if (!viewport) return;
    navStartRef.current = {
      camera: viewport.getCamera(),
      zoom:   viewport.getZoom?.(),
      p:      svgPointFromEvent(e),
    };
  };

  const updateNavGesture = (e) => {
    if (!navStartRef.current) return;
    const engine = cornerstone.getRenderingEngine(engineId);
    const viewport = engine?.getViewport(viewportId);
    if (!viewport) return;
    const p = svgPointFromEvent(e);
    const dx = p.x - navStartRef.current.p.x;
    const dy = p.y - navStartRef.current.p.y;
    const startCam = navStartRef.current.camera;

    if (activeTool === TOOL_ZOOM) {
      // Drag-down (dy > 0) zooms in (zoom value increases). Use viewport.setZoom
      // when available — setCamera({parallelScale}) gets normalized away by the
      // displayArea on this cornerstone3D version.
      const factor = Math.exp(dy / 300);
      if (typeof viewport.setZoom === "function" && navStartRef.current.zoom != null) {
        const newZoom = Math.max(0.1, navStartRef.current.zoom * factor);
        viewport.setZoom(newZoom);
      } else {
        const newScale = Math.max(1, startCam.parallelScale / factor);
        viewport.setCamera({ ...startCam, parallelScale: newScale });
      }
      viewport.render();
    } else if (activeTool === TOOL_PAN) {
      const canvas = viewport.getCanvas();
      const worldPerPx = (startCam.parallelScale * 2) / (canvas?.height || 1);
      const wdx = -dx * worldPerPx;
      const wdy = -dy * worldPerPx;
      viewport.setCamera({
        ...startCam,
        focalPoint: [startCam.focalPoint[0] + wdx, startCam.focalPoint[1] + wdy, startCam.focalPoint[2]],
        position:   [startCam.position[0]   + wdx, startCam.position[1]   + wdy, startCam.position[2]],
      });
      viewport.render();
    }
  };

  const endNavGesture = () => { navStartRef.current = null; };

  // Pinch helpers — distance between the first two active pointers + their midpoint.
  // Used to compute the zoom scale factor (distance ratio) and the 2-finger pan
  // delta (midpoint movement) during a pinch gesture on mobile / touch devices.
  const getPinchMetrics = () => {
    const pts = Array.from(activePointersRef.current.values());
    if (pts.length < 2) return null;
    const [a, b] = pts;
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return {
      distance: Math.sqrt(dx * dx + dy * dy),
      midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  };

  const handleOverlayDoubleClick = (e) => {
    if (activeTool !== TOOL_FLEXPOLY || !pendingPolygon) return;
    if (pendingPolygon.points.length >= 3) {
      commitToHistory([...shapes, { type: TOOL_FLEXPOLY, points: pendingPolygon.points, strokeWidth: activeStrokeWidth }]);
    }
    setPendingPolygon(null);
    setPreviewPoint(null);
    e.preventDefault();
  };

  useEffect(() => {
    if (!pendingPolygon) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") { setPendingPolygon(null); setPreviewPoint(null); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pendingPolygon]);

  // Close the stroke-thickness dropdown when the user clicks anywhere outside it.
  useEffect(() => {
    if (!strokeDropdownOpen) return;
    const onDocPointerDown = (e) => {
      if (strokeDropdownRef.current && !strokeDropdownRef.current.contains(e.target)) {
        setStrokeDropdownOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [strokeDropdownOpen]);

  const handleOverlayMouseDown = (e) => {
    if (!isEditMode || !activeTool) return;
    if (e.button !== 0) return;
    const p = svgPointFromEvent(e);

    if (activeTool === TOOL_TEXT) {
      const text = window.prompt("Enter annotation text:", "");
      if (text && text.trim()) {
        commitToHistory([...shapes, { type: TOOL_TEXT, x: p.x, y: p.y, text: text.trim(), strokeWidth: activeStrokeWidth }]);
      }
      e.preventDefault();
      return;
    }

    if (activeTool === TOOL_FLEXPOLY) {
      if (!pendingPolygon) {
        setPendingPolygon({ points: [[p.x, p.y]] });
      } else {
        const pts = pendingPolygon.points;
        const [fx, fy] = pts[0];
        const distToFirst = Math.hypot(p.x - fx, p.y - fy);
        if (pts.length >= 3 && distToFirst <= FLEXPOLY_CLOSE_TOL) {
          commitToHistory([...shapes, { type: TOOL_FLEXPOLY, points: pts, strokeWidth: activeStrokeWidth }]);
          setPendingPolygon(null);
          setPreviewPoint(null);
        } else {
          setPendingPolygon({ points: [...pts, [p.x, p.y]] });
        }
      }
      e.preventDefault();
      return;
    }

    if (activeTool === TOOL_ZOOM || activeTool === TOOL_PAN) {
      beginNavGesture(e);
      e.preventDefault();
      return;
    }

    if (DRAW_TOOLS.has(activeTool)) {
      setDrawing(createInitialShape(activeTool, p));
      setSelectedIndex(null);
      e.preventDefault();
    }
  };

  const handleOverlayMouseMove = (e) => {
    if (moveStart && selectedIndex !== null) {
      const p = svgPointFromEvent(e);
      const dx = p.x - moveStart.startX;
      const dy = p.y - moveStart.startY;
      setShapes((prev) => prev.map((s, i) => (i === selectedIndex ? translateShape(moveStart.shape, dx, dy) : s)));
      return;
    }
    if (drawing) {
      setDrawing(updateDrawingShape(drawing, svgPointFromEvent(e)));
      return;
    }
    if (activeTool === TOOL_FLEXPOLY && pendingPolygon) {
      setPreviewPoint(svgPointFromEvent(e));
      return;
    }
    if (navStartRef.current) {
      updateNavGesture(e);
    }
  };

  const handleOverlayMouseUp = () => {
    if (moveStart && selectedIndex !== null) {
      const moved = shapes[selectedIndex];
      const before = moveStart.shape;
      const changed = JSON.stringify(moved) !== JSON.stringify(before);
      if (changed) commitToHistory(shapes);
      setMoveStart(null);
      return;
    }
    if (drawing) {
      if (isShapeMeaningful(drawing)) {
        let shapeToCommit = drawing;
        if (drawing.type === TOOL_ARROW) {
          const text = window.prompt("Annotation label (optional — leave blank for arrow only):", "");
          if (text && text.trim()) shapeToCommit = { ...drawing, text: text.trim() };
        }
        commitToHistory([...shapes, shapeToCommit]);
      }
      setDrawing(null);
      return;
    }
    if (navStartRef.current) endNavGesture();
  };

  const ShapeElement = ({ shape, selected }) => {
    const color = selected ? SHAPE_STROKE_SELECTED : SHAPE_STROKE;
    // Per-shape stroke width — shapes saved with the old format (no strokeWidth field)
    // fall back to the original default so previously-saved annotations look unchanged.
    const sw = shape.strokeWidth ?? SHAPE_STROKE_WIDTH;
    const stroke = { stroke: color, strokeWidth: sw, fill: "none" };
    switch (shape.type) {
      case TOOL_RECT: {
        const x = Math.min(shape.x1, shape.x2);
        const y = Math.min(shape.y1, shape.y2);
        const w = Math.abs(shape.x2 - shape.x1);
        const h = Math.abs(shape.y2 - shape.y1);
        return <rect x={x} y={y} width={w} height={h} {...stroke} />;
      }
      case TOOL_ELLIPSE: {
        const cx = (shape.x1 + shape.x2) / 2;
        const cy = (shape.y1 + shape.y2) / 2;
        const rx = Math.abs(shape.x2 - shape.x1) / 2;
        const ry = Math.abs(shape.y2 - shape.y1) / 2;
        return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...stroke} />;
      }
      case TOOL_CIRCLE:
        return <circle cx={shape.cx} cy={shape.cy} r={shape.r} {...stroke} />;
      case TOOL_LINE:
        return <line x1={shape.x1} y1={shape.y1} x2={shape.x2} y2={shape.y2} {...stroke} />;
      case TOOL_BIDIR: {
        const lDx = shape.x2 - shape.x1;
        const lDy = shape.y2 - shape.y1;
        const length = Math.sqrt(lDx * lDx + lDy * lDy);
        if (length === 0) {
          return <circle cx={shape.x1} cy={shape.y1} r="4" fill="none" stroke={color} strokeWidth={sw} />;
        }
        const midX = (shape.x1 + shape.x2) / 2;
        const midY = (shape.y1 + shape.y2) / 2;
        const perpX = -lDy / length;
        const perpY = lDx / length;
        const shortHalf = length * 0.4;
        const sx1 = midX + perpX * shortHalf;
        const sy1 = midY + perpY * shortHalf;
        const sx2 = midX - perpX * shortHalf;
        const sy2 = midY - perpY * shortHalf;
        return (
          <g>
            <line x1={shape.x1} y1={shape.y1} x2={shape.x2} y2={shape.y2} {...stroke} />
            <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} {...stroke} />
          </g>
        );
      }
      case TOOL_ARROW: {
        const angle = Math.atan2(shape.y2 - shape.y1, shape.x2 - shape.x1);
        const headLen   = 8 + sw * 2;
        const headHalfW = 3 + sw;
        const ax1 = shape.x2 - headLen * Math.cos(angle) + headHalfW * Math.sin(angle);
        const ay1 = shape.y2 - headLen * Math.sin(angle) - headHalfW * Math.cos(angle);
        const ax2 = shape.x2 - headLen * Math.cos(angle) - headHalfW * Math.sin(angle);
        const ay2 = shape.y2 - headLen * Math.sin(angle) + headHalfW * Math.cos(angle);
        const lineEndX = shape.x2 - headLen * Math.cos(angle);
        const lineEndY = shape.y2 - headLen * Math.sin(angle);
        // Label + dotted leader anchored at the TAIL of the arrow (shape.x1,shape.y1),
        // not the arrowhead. Keeps the text out of the way of whatever the arrow points at.
        const labelX = shape.x1 + 12;
        const labelY = shape.y1 + 40;
        return (
          <g>
            <line x1={shape.x1} y1={shape.y1} x2={lineEndX} y2={lineEndY} {...stroke} />
            <polygon points={`${shape.x2},${shape.y2} ${ax1},${ay1} ${ax2},${ay2}`} fill={color} />
            {shape.text && (
              <>
                <line x1={shape.x1} y1={shape.y1} x2={labelX} y2={labelY - 4}
                      stroke={color} strokeWidth="1" strokeDasharray="2,3" />
                <text x={labelX} y={labelY} fill={color} fontSize="14" fontFamily="sans-serif" fontWeight="bold">
                  {shape.text}
                </text>
              </>
            )}
          </g>
        );
      }
      case TOOL_FREEHAND: {
        const d = shape.points.map((p, i) => (i === 0 ? "M" : "L") + p[0] + "," + p[1]).join(" ");
        return <path d={d} {...stroke} />;
      }
      case TOOL_FLEXPOLY: {
        const pointsStr = shape.points.map(([x, y]) => `${x},${y}`).join(" ");
        return <polygon points={pointsStr} {...stroke} />;
      }
      case TOOL_TEXT:
        return (
          <text x={shape.x} y={shape.y} fill={color} fontSize="14" fontFamily="sans-serif" fontWeight="bold">
            {shape.text}
          </text>
        );
      default:
        return null;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Save / Reset
  // ─────────────────────────────────────────────────────────────────────────

  // Gate the save with a gender-conflict check when the feature is sex-restricted.
  // The actual save work lives in performSave so the confirm popup's Continue button
  // can call it directly without re-entering this gate.
  const handleSave = () => {
    // Empty-shapes (Reset → Save) is treated as "clear my saved image": call the backend
    // delete endpoint to null the storage key. This MUST come before the gender check —
    // if there are no annotations to save, there's no gender mismatch to warn about
    // either, and the dot on the button should clear regardless of patient sex.
    if (shapes.length === 0 && deleteFunc) {
      performClear();
      return;
    }
    // Gender-conflict gate — only applies when the user is actually trying to save
    // annotations on the image (shapes.length > 0). patientSex is sourced from
    const normalizedSex = typeof patientSex === "string"
      ? patientSex.trim().toUpperCase().charAt(0)
      : "";
    if (femaleOnly && normalizedSex === "M") {
      setShowSexWarning(true);
      return;
    }
    performSave();
  };

  // Calls the backend delete endpoint so the toolbar's green "edited" indicator clears.
  // After success, reload the plain reference image and reset modal state. Triggered
  // when Save is clicked with no annotations on the canvas (typically Reset → Save).
  const performClear = async () => {
    setIsSaving(true);
    try {
      await deleteFunc(studyParentId);
      const defaultImageId = await fetchImageId({ forceDefault: true });
      await loadImage(defaultImageId, { resetCamera: true });
      setShapes([]);
      setDrawing(null);
      setSelectedIndex(null);
      setMoveStart(null);
      setPendingPolygon(null);
      setPreviewPoint(null);
      setHistory([{ shapes: [], imageId: defaultImageId }]);
      setHistoryIndex(0);
      setIsEditMode(false);
      setActiveTool(null);
    } catch (err) {
      console.error(`${logTag} clear error:`, err);
      showToastv2(TOAST_TYPE.ERROR, `Error while clearing ${errorLabel}`);
    } finally { setIsSaving(false); }
  };

  const performSave = async () => {
    setIsSaving(true);
    try {
      const engine   = cornerstone.getRenderingEngine(engineId);
      const viewport = engine?.getViewport(viewportId);
      if (!viewport) throw new Error("Viewport not available");

      const cornerstoneCanvas = viewport.getCanvas();
      const svgEl = svgOverlayRef.current;
      if (!cornerstoneCanvas || !svgEl) throw new Error("Canvas or SVG overlay not found");

      const W = cornerstoneCanvas.width;
      const H = cornerstoneCanvas.height;
      if (W === 0 || H === 0) throw new Error("Canvas has zero dimensions");

      const overlayRect = svgEl.getBoundingClientRect();

      // Load the CURRENT SOURCE IMAGE at its natural pixel dimensions. Previously
      // we composited onto `cornerstoneCanvas` directly, which captures only the
      // viewport-visible region (i.e. the zoomed crop) — so re-opening the modal
      // showed only that crop, with no way to zoom out. Now we composite onto a
      // canvas the size of the original image and project annotations into image-
      // pixel space, so the saved JPEG is the full reference image with annotations
      // burned in. Reopen → user sees the full image at fit, can zoom anywhere.
      const currentImageId = currentImageIdRef.current;
      if (!currentImageId || !currentImageId.startsWith(`${loaderScheme}:`)) {
        throw new Error("Current image id not available");
      }
      const sourceBlobUrl = currentImageId.substring(loaderScheme.length + 1);
      const sourceImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload  = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load source image for save"));
        img.src     = sourceBlobUrl;
      });
      const imgW = sourceImg.naturalWidth;
      const imgH = sourceImg.naturalHeight;
      if (imgW === 0 || imgH === 0) throw new Error("Source image has zero dimensions");

      // Compute the canvas-pixel → image-pixel affine transform from the viewport's
      // current camera state. canvasToWorld maps a canvas pixel to its world coord,
      // and for a stack viewport world coords are image pixels. Sampling the two
      // canvas corners gives us a pure scale+translate transform that we can drop
      // straight into an SVG <g transform="matrix(...)"> wrapper. This is what
      // makes a shape drawn at canvas pixel (200, 100) under zoom 2x land at the
      // correct anatomical position in the full image.
      const A = viewport.canvasToWorld([0, 0]);
      const B = viewport.canvasToWorld([W, H]);
      const sx = (B[0] - A[0]) / W;
      const sy = (B[1] - A[1]) / H;
      const tx = A[0];
      const ty = A[1];

      const ns = "http://www.w3.org/2000/svg";
      const clonedSvg = svgEl.cloneNode(true);
      // Move all SVG children into a <g> with the canvas→image transform applied.
      const g = document.createElementNS(ns, "g");
      g.setAttribute("transform", `matrix(${sx} 0 0 ${sy} ${tx} ${ty})`);
      while (clonedSvg.firstChild) g.appendChild(clonedSvg.firstChild);
      clonedSvg.appendChild(g);
      clonedSvg.setAttribute("width",   imgW);
      clonedSvg.setAttribute("height",  imgH);
      clonedSvg.setAttribute("viewBox", `0 0 ${imgW} ${imgH}`);
      clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const svgDataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(clonedSvg))))}`;

      // Offscreen canvas sized to the SOURCE IMAGE (not the viewport).
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width  = imgW;
      tempCanvas.height = imgH;
      const ctx = tempCanvas.getContext("2d");
      ctx.drawImage(sourceImg, 0, 0, imgW, imgH);

      const mergedFile = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          // SVG already has the transform baked in, so it rasterises at exactly
          // imgW × imgH with annotations in the right places.
          ctx.drawImage(img, 0, 0, imgW, imgH);
          tempCanvas.toBlob((blob) => {
            if (!blob) return reject(new Error("toBlob returned null"));
            resolve(new File([blob], outputFileName, { type: "image/jpeg" }));
          }, "image/jpeg", 0.95);
        };
        img.onerror = () => reject(new Error("SVG rasterization failed"));
        img.src = svgDataUrl;
      });

      await uploadFunc(studyParentId, mergedFile);

      // Refresh the viewport to show the merged JPEG immediately — uses the local blob
      // rather than waiting on a server round-trip.
      const mergedBlobUrl = URL.createObjectURL(mergedFile);
      blobUrlsRef.current.push(mergedBlobUrl);
      const mergedImageId = `${loaderScheme}:${mergedBlobUrl}`;
      await loadImage(mergedImageId, { resetCamera: true });

      setShapes([]);
      setDrawing(null);
      setSelectedIndex(null);
      setMoveStart(null);
      setPendingPolygon(null);
      setPreviewPoint(null);
      setHistory([{ shapes: [], imageId: mergedImageId }]);
      setHistoryIndex(0);
      setIsEditMode(false);
      setActiveTool(null);
    } catch (err) {
      console.error(`${logTag} save error:`, err);
      showToastv2(TOAST_TYPE.ERROR, `Error while saving ${errorLabel}`);
    } finally { setIsSaving(false); }
  };

  const handleReset = async () => {
    setDrawing(null);
    setSelectedIndex(null);
    setMoveStart(null);
    setPendingPolygon(null);
    setPreviewPoint(null);
    setIsLoading(true);
    try {
      const defaultImageId = await fetchImageId({ forceDefault: true });
      await loadImage(defaultImageId, { resetCamera: true });
      commitToHistory([], defaultImageId);
    } catch (err) {
      console.error(`${logTag} reset error:`, err);
      showToastv2(TOAST_TYPE.ERROR, `Error while resetting ${errorLabel}`);
    } finally { setIsLoading(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  const overlayActive = isEditMode && !!activeTool;
  const cursorForTool = (() => {
    if (!overlayActive) return "default";
    if (activeTool === TOOL_PAN)  return "grab";
    if (activeTool === TOOL_ZOOM) return "ns-resize";
    if (activeTool === TOOL_TEXT) return "text";
    return "crosshair";
  })();

  const PrimaryButtonList = isEditMode
    ? [{ title: isSaving ? "Saving..." : t("button.save"), disable: isSaving, onclick: handleSave }]
    : [{ title: t("button.edit"),                          disable: isLoading,  onclick: handleEdit }];

  // Cancel appears in BOTH modes — clicking it always closes the modal. In edit mode,
  // any in-progress shapes are discarded (same behavior as closing via the header X).
  // Reset stays edit-only since outside Edit mode there's nothing to reset.
  // Clear (was "Reset") is now a destructive, confirmed action. Clicking it opens
  // a confirmation popup; only on Continue do we actually delete the saved image
  // and reset the modal state via performClear. Cancel stays the same.
  const DefaultButtonList = isEditMode
    ? [
        { title: t("button.cancel"), disable: isSaving,  onclick: handleClose },
        { title: t("button.clear"),  disable: isLoading || isSaving, onclick: () => setShowClearConfirm(true) },
      ]
    : [
        { title: t("button.cancel"), disable: false,     onclick: handleClose },
      ];

  const themedStyles = {
    body: {
      background: getThemeColor("modal-body"),
      color:      getThemeColor("primary-color"),
      display:    "flex",
      flexDirection: "column",
      width:      "100%",
    },
    toolbar: {
      padding:      "8px 12px",
      display:      "flex",
      gap:          "6px",
      flexWrap:     "wrap",
      alignItems:   "center",
      minHeight:    "42px",
      background:   getThemeColor("action-details-background"),
      borderBottom: getThemeColor("border"),
    },
    toolBtn: (active, enabled) => ({
      padding:      "6px 8px",
      borderRadius: "4px",
      border:       `1px solid ${active ? getThemeColor("report-active-tab") : "transparent"}`,
      background:   active ? getThemeColor("report-active-tab") : getThemeColor("popup-hover"),
      cursor:       enabled ? "pointer" : "not-allowed",
      opacity:      enabled ? 1 : 0.5,
      transition:   "background 0.15s",
      display:      "flex",
      alignItems:   "center",
      justifyContent: "center",
      minWidth:     "32px",
      minHeight:    "30px",
    }),
    iconBtn: (enabled) => ({
      padding:      "6px 8px",
      borderRadius: "4px",
      border:       "1px solid transparent",
      background:   getThemeColor("popup-hover"),
      cursor:       enabled ? "pointer" : "not-allowed",
      display:      "flex",
      alignItems:   "center",
      justifyContent: "center",
      minWidth:     "32px",
      minHeight:    "30px",
      opacity:      enabled ? 1 : 0.4,
    }),
    divider: {
      width: "1px",
      height: "22px",
      background: getThemeColor("border")?.toString().includes("solid")
        ? "#555"
        : getThemeColor("border"),
      margin: "0 4px",
      opacity: 0.6,
    },
    viewportWrapper: {
      position:   "relative",
      background: "#000000",
      height:     "460px",
      width:      "100%",
      flexShrink: 0,
      overflow:   "visible",
      touchAction: "none",
    },
    viewport:       { width: "100%", height: "460px" },
    svgOverlay:     {
      position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5,
      pointerEvents: overlayActive ? "auto" : "none",
      touchAction:   overlayActive ? "none" : "auto",
      cursor:        cursorForTool,
    },
    loadingOverlay: {
      position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
      color: "#FFFFFF", fontSize: "14px", zIndex: 1, pointerEvents: "none",
    },
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const renderToolIcon = (toolName, active) => {
    const color = active ? theme.palette.activeTool : theme.palette.inactiveTool;
    switch (toolName) {
      case TOOL_ARROW:    return <Annotation       color={color} />;
      case TOOL_FREEHAND: return <PencilIconInline color={color} />;
      case TOOL_FLEXPOLY: return <PolygonIcon      color={color} />;
      case TOOL_CIRCLE:   return <Circle           color={color} />;
      case TOOL_LINE:     return <LineIconInline   color={color} />;
      case TOOL_BIDIR:    return <Bidirectional color={color} />;
      case TOOL_ELLIPSE:  return <Ellipse       color={color} />;
      case TOOL_RECT:     return <Rectangle     color={color} />;
      case TOOL_ZOOM:     return <Zoom          color={color} />;
      case TOOL_PAN:      return <Pan           color={color} />;
      case TOOL_TEXT:
        return (
          <span style={{
            display: "inline-block", width: 16, height: 16, lineHeight: "16px",
            textAlign: "center", fontFamily: "serif", fontStyle: "italic",
            fontWeight: 700, fontSize: 14, color,
          }}>T</span>
        );
      default: return null;
    }
  };

  const modalBody = (
    <div style={themedStyles.body}>
      <div style={themedStyles.toolbar}>
        <button title="Undo" disabled={!isEditMode || !canUndo} onClick={handleUndo}
                style={themedStyles.iconBtn(isEditMode && canUndo)}>
          <RotateLeft  color={isEditMode && canUndo ? theme.palette.activeTool : theme.palette.inactiveTool} />
        </button>
        <button title="Redo" disabled={!isEditMode || !canRedo} onClick={handleRedo}
                style={themedStyles.iconBtn(isEditMode && canRedo)}>
          <RotateRight color={isEditMode && canRedo ? theme.palette.activeTool : theme.palette.inactiveTool} />
        </button>

        {/* Stroke thickness dropdown — the trigger shows the currently-selected width
            as a small line preview. Clicking opens a popover with all width options,
            each rendered with the same line-diagram style. Picked thickness only affects
            shapes drawn AFTER the click; existing shapes keep their original width. */}
        <span style={themedStyles.divider} />
        <div ref={strokeDropdownRef} style={{ position: "relative" }}>
          <button
            title={`Stroke ${activeStrokeWidth}px`}
            disabled={!isEditMode}
            onClick={() => setStrokeDropdownOpen((o) => !o)}
            style={themedStyles.toolBtn(strokeDropdownOpen, isEditMode)}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <span style={{
                display:      "inline-block",
                width:        30,
                height:       activeStrokeWidth,
                background:   isEditMode ? theme.palette.activeTool : theme.palette.inactiveTool,
                borderRadius: activeStrokeWidth / 2,
              }} />
              <span style={{
                fontSize: "14px",
                lineHeight: 1,
                color: isEditMode ? theme.palette.activeTool : theme.palette.inactiveTool,
              }}>▾</span>
            </span>
          </button>
          {strokeDropdownOpen && (
            <div style={{
              position:     "absolute",
              top:          "calc(100% + 4px)",
              left:         0,
              minWidth:     "60px",
              background:   getThemeColor("modal-body"),
              border:       `1px solid ${getThemeColor("border")?.toString().includes("solid") ? "#555" : (getThemeColor("border") || "#555")}`,
              borderRadius: "4px",
              padding:      "4px",
              display:      "flex",
              flexDirection: "column",
              gap:          "2px",
              boxShadow:    "0 2px 8px rgba(0,0,0,0.25)",
              zIndex:       100,
            }}>
              {STROKE_WIDTH_OPTIONS.map((w) => {
                const active = activeStrokeWidth === w;
                const iconColor = active ? theme.palette.activeTool : theme.palette.inactiveTool;
                return (
                  <button
                    key={w}
                    title={`Stroke ${w}px`}
                    onClick={() => { setActiveStrokeWidth(w); setStrokeDropdownOpen(false); }}
                    style={{
                      ...themedStyles.toolBtn(active, true),
                      minWidth: "44px",
                      padding:  "6px 10px",
                    }}
                  >
                    <span style={{
                      display:      "inline-block",
                      width:        38,
                      height:       w,
                      background:   iconColor,
                      borderRadius: w / 2,
                    }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {TOOLBAR_GROUPS.map((group, gi) => {
          const isZoomPanGroup = gi === 0;
          return (
            <span key={gi} style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
              {isZoomPanGroup && <span style={themedStyles.divider} />}
              {group.map(({ name, label }) => (
                <button
                  key={name}
                  title={label}
                  disabled={!isEditMode}
                  onClick={() => handleToolSelect(name)}
                  style={themedStyles.toolBtn(activeTool === name, isEditMode)}
                >
                  {renderToolIcon(name, activeTool === name)}
                </button>
              ))}
              {isZoomPanGroup && <span style={themedStyles.divider} />}
            </span>
          );
        })}
      </div>

      <div style={themedStyles.viewportWrapper}>
        {isLoading && <div style={themedStyles.loadingOverlay}>Loading image...</div>}
        <div ref={viewportDivRef} style={themedStyles.viewport} />
        <svg
          ref={svgOverlayRef}
          style={themedStyles.svgOverlay}
          onPointerDown={(e) => {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
            activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
            if (activePointersRef.current.size === 2 && activeTool === TOOL_ZOOM) {
              const engine = cornerstone.getRenderingEngine(engineId);
              const viewport = engine?.getViewport(viewportId);
              const metrics = getPinchMetrics();
              if (viewport && metrics) {
                pinchStartRef.current = {
                  camera:   viewport.getCamera(),
                  zoom:     viewport.getZoom?.(),
                  distance: metrics.distance,
                  midpoint: metrics.midpoint,
                };
              }
              setDrawing(null);
              navStartRef.current = null;
              return;
            }

            if (activePointersRef.current.size === 1) {
              handleOverlayMouseDown(e);
            }
          }}
          onPointerMove={(e) => {
            if (activePointersRef.current.has(e.pointerId)) {
              activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
            }

            if (activePointersRef.current.size >= 2 && pinchStartRef.current) {
              // Pinch-zoom ONLY. Two-finger pan was removed per UX: pan stays as
              // a single-finger gesture via the Pan tool.
              const engine = cornerstone.getRenderingEngine(engineId);
              const viewport = engine?.getViewport(viewportId);
              if (!viewport) return;
              const metrics = getPinchMetrics();
              if (!metrics || metrics.distance <= 0) return;

              const startCam    = pinchStartRef.current.camera;
              // Fingers spreading apart (distance grows) → zoom in.
              const zoomFactor  = metrics.distance / pinchStartRef.current.distance;
              if (typeof viewport.setZoom === "function" && pinchStartRef.current.zoom != null) {
                const newZoom = Math.max(0.1, pinchStartRef.current.zoom * zoomFactor);
                viewport.setZoom(newZoom);
              } else {
                const newScale = Math.max(1, startCam.parallelScale / zoomFactor);
                viewport.setCamera({ ...startCam, parallelScale: newScale });
              }
              viewport.render();
              return;
            }

            if (activePointersRef.current.size === 1) {
              handleOverlayMouseMove(e);
            }
          }}
          onPointerUp={(e) => {
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
            activePointersRef.current.delete(e.pointerId);

            if (pinchStartRef.current && activePointersRef.current.size < 2) {
              // Pinch ended (one or both fingers lifted). Don't auto-resume any
              // single-finger gesture — user must fully lift and re-touch to draw.
              pinchStartRef.current = null;
              setDrawing(null);
              navStartRef.current = null;
              return;
            }

            if (activePointersRef.current.size === 0) {
              handleOverlayMouseUp(e);
            }
          }}
          onPointerCancel={(e) => {
            activePointersRef.current.delete(e.pointerId);
            if (activePointersRef.current.size < 2) {
              pinchStartRef.current = null;
            }
            if (activePointersRef.current.size === 0) {
              handleOverlayMouseUp(e);
            }
          }}
          onDoubleClick={handleOverlayDoubleClick}
        >
          {shapes.map((shape, i) => <ShapeElement key={i} shape={shape} selected={i === selectedIndex} />)}
          {drawing && <ShapeElement shape={drawing} />}
          {pendingPolygon && (() => {
            const pts = pendingPolygon.points;
            const last = pts[pts.length - 1];
            return (
              <g pointerEvents="none">
                {pts.length >= 2 && (
                  <polyline
                    points={pts.map(([x, y]) => `${x},${y}`).join(" ")}
                    fill="none"
                    stroke={SHAPE_STROKE}
                    strokeWidth={activeStrokeWidth}
                  />
                )}
                {previewPoint && last && (
                  <line
                    x1={last[0]} y1={last[1]}
                    x2={previewPoint.x} y2={previewPoint.y}
                    stroke={SHAPE_STROKE}
                    strokeWidth={activeStrokeWidth}
                    strokeDasharray="4,4"
                  />
                )}
                {pts.map(([x, y], i) => (
                  <circle
                    key={i}
                    cx={x} cy={y}
                    r={i === 0 ? 5 : 3}
                    fill={i === 0 ? SHAPE_STROKE : "none"}
                    stroke={SHAPE_STROKE}
                    strokeWidth="1.5"
                  />
                ))}
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );

  const modalDetails = {
    header: <QCModalHeader heading={title} handleCancel={handleClose} isFistolugramHeader={true} />,
    body:   modalBody,
    footer: <QCModalFooter PrimaryButtonList={PrimaryButtonList} DefaultButtonList={DefaultButtonList} />,
    width:  "880px",
  };

  // Gender-conflict confirm dialog — mirrors the report-submit ConfirmAlertModal UX.
  // Cancel hides only the popup (modal stays open in Edit mode); Continue runs performSave.
  const sexWarningModal = showSexWarning ? {
    header: <QCModalHeader heading="Alert" handleCancel={() => setShowSexWarning(false)} isFistolugramHeader={true} />,
    body: (
      <div style={{
        padding: "20px 24px",
        color: getThemeColor("primary-color"),
        background: getThemeColor("modal-body"),
        fontSize: "14px",
        lineHeight: "1.5",
      }}>
        {/* {`This patient is recorded as Male, but ${errorLabel} mapping is typically used for female patients. Do you want to save this ${errorLabel} mapping anyway?`} */}
        {`A potential mismatch has been identified between the patient sex and the ${errorLabel} mapping annotation. Do you want to proceed saving ${errorLabel} mapping?`}
      </div>
    ),
    footer: (
      <QCModalFooter
        DefaultButtonList={[{ title: t("button.cancel"), disable: false, onclick: () => setShowSexWarning(false) }]}
        PrimaryButtonList={[{ title: t("button.continue"), disable: false, onclick: () => {
          setShowSexWarning(false);
          performSave();
        }}]}
      />
    ),
    width: "500px",
  } : null;

  // Clear-confirmation popup. Triggered by the Clear button in Edit mode. On Continue,
  // performClear deletes the saved image on the backend, reloads the default reference,
  // and exits Edit mode in one shot. Cancel just hides this popup; the user stays in
  // Edit mode with their work intact.
  const clearConfirmModal = showClearConfirm ? {
    header: (
      <QCModalHeader
        heading="Alert"
        handleCancel={() => setShowClearConfirm(false)}
        isFistolugramHeader={true}
      />
    ),
    body: (
      <div style={{
        padding: "20px 24px",
        color: getThemeColor("primary-color"),
        background: getThemeColor("modal-body"),
        fontSize: "14px",
        lineHeight: "1.5",
      }}>
        {t("string-literals.clear-annotation-confirm", { feature: featureLabel })}
      </div>
    ),
    footer: (
      <QCModalFooter
        DefaultButtonList={[
          { title: t("button.cancel"), disable: false, onclick: () => setShowClearConfirm(false) },
        ]}
        PrimaryButtonList={[
          { title: t("button.continue"), disable: false, onclick: () => {
              setShowClearConfirm(false);
              performClear();
          }},
        ]}
      />
    ),
    width: "500px",
  } : null;

  return (
    <>
      <ModalMain showModal={true} modalDetails={modalDetails} />
      {sexWarningModal && <ModalMain showModal={true} modalDetails={sexWarningModal} />}
      {clearConfirmModal && <ModalMain showModal={true} modalDetails={clearConfirmModal} />}
    </>
  );
};

export default AnnotatedImageModal;